import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatBirthDate } from '@/util/date.ts';
import { MentalHealthReport } from '@/api/mentalHealth/report.types';
import { IUser } from '@/api/user/getUsers';
import { calculateAge } from '@/util/person';
import VitalSignsChart from '@/component/mentalHealth/VitalSignsChart';
import { SymptomPredictionCard } from '@/component/mentalHealth/SymptomPredictionCard';
import { formatRounded, numberOr } from '@/util/mentalHealthFormat';
import { useAppLocale } from '@/locales/locale';

interface MentalHealthReportPDFTemplateProps {
    report: MentalHealthReport;
    user?: IUser;
}

const MentalHealthReportPDFTemplate: React.FC<MentalHealthReportPDFTemplateProps> = ({ report, user }) => {
    const { t } = useTranslation();
    const { intlLocale } = useAppLocale();

    // Helper to translate values
    const translateValue = (value: string | undefined, prefix?: string): string => {
        if (!value) return '';
        let translationKey: string;
        if (value.startsWith('mentalHealth.')) {
            translationKey = value;
        } else if (prefix) {
            translationKey = `${prefix}.${value}`;
        } else {
            return value;
        }
        const translated = t(translationKey);
        return translated === translationKey ? value : translated;
    };

    const genderName = (gender: number) => {
        if (gender === 0) return t('users.list.item.gender.male');
        if (gender === 1) return t('users.list.item.gender.female');
        return t('users.list.item.gender.none');
    };

    return (
        <div className="p-8 bg-white text-black w-[1000px] mx-auto space-y-8" id="pdf-template">
            {/* 주의사항 배너 */}
            <div data-pdf-block className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                    <span className="font-bold">⚠️ {t('mentalHealth.reportDetail.importantNotice')}:</span> {t('mentalHealth.reportDetail.disclaimerText')}
                </p>
            </div>

            {/* Header */}
            <div data-pdf-block className="border-b-2 border-gray-800 pb-4 mb-8 flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">{t('mentalHealth.reportSummary.title')}</h1>
                    <p className="text-gray-600 mt-1">{t('mentalHealth.reportDetail.subtitle')}</p>
                </div>
                <div className="text-right">
                    <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.analysisDate')}</div>
                    <div className="text-lg font-semibold text-gray-700">
                        {new Date().toLocaleDateString(intlLocale, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}
                    </div>
                </div>
            </div>

            {/* User Info Section */}
            {user && (
                <div data-pdf-block className="bg-gray-50 rounded-xl border border-gray-200 p-6">
                    <h2 data-pdf-heading className="text-xl font-bold text-gray-800 mb-4 border-l-4 border-gray-600 pl-3">{t('users.detail.basicInfo')}</h2>
                    <div className="grid grid-cols-2 gap-8">
                        {/* Left Column: Basic Info & Contact */}
                        <div className="space-y-6">
                            <div>
                                <h3 className="font-semibold text-gray-700 mb-2">{t('users.detail.info')}</h3>
                                <div className="bg-white p-4 rounded-lg border border-gray-100 space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('users.detail.name')}</span>
                                        <span className="font-medium">{user.name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('users.detail.birthDate')}</span>
                                        <span className="font-medium">
                                            {formatBirthDate(user.birthDate)} ({t('users.list.item.age', { count: calculateAge(user.birthDate) })})
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('users.detail.gender')}</span>
                                        <span className="font-medium">{genderName(user.gender)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('users.detail.address')}</span>
                                        <span className="font-medium text-right max-w-[200px]">{user.address || '-'}</span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-700 mb-2">{t('users.detail.contact')}</h3>
                                <div className="bg-white p-4 rounded-lg border border-gray-100 space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('users.detail.contact.self')}</span>
                                        <span className="font-medium">{user.contact || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{user.primaryRelation || t('users.detail.contact.guardian')}</span>
                                        <span className="font-medium">{user.primaryContact || '-'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Notes/Caution */}
                        <div>
                            <h3 className="font-semibold text-gray-700 mb-2">{t('users.detail.memo')}</h3>
                            <div className="bg-white p-4 rounded-lg border border-gray-100 h-full min-h-[150px]">
                                <p className="text-gray-700 whitespace-pre-wrap">
                                    {user.inspectorMemo || t('common.noData')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Overview Section */}
            <div className="space-y-6">
                <h2 data-pdf-heading className="text-2xl font-bold border-l-4 border-blue-600 pl-4">{t('mentalHealth.detail.overview')}</h2>

                <div data-pdf-block className="grid grid-cols-3 gap-6">
                    {/* Mind Score */}
                    <div className="col-span-1 flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl border border-gray-200">
                        <div className="text-lg font-bold text-gray-700 mb-2">{t('mentalHealth.detail.mindHealthScore')}</div>
                        <div className={`text-6xl font-black mb-2 ${numberOr(report.mindScore.score) >= 80 ? 'text-emerald-600' :
                            numberOr(report.mindScore.score) >= 70 ? 'text-blue-600' :
                                numberOr(report.mindScore.score) >= 55 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                            {formatRounded(report.mindScore.score)}
                        </div>
                        <div className={`px-4 py-1 rounded-full text-sm font-bold text-white ${report.decisionLabel === 'healthy' ? 'bg-emerald-500' :
                            report.decisionLabel === 'normal' ? 'bg-blue-500' :
                                report.decisionLabel === 'caution' ? 'bg-amber-500' : 'bg-red-500'
                            }`}>
                            {t(`mentalHealth.status.${report.decisionLabel}`)}
                        </div>
                    </div>

                    {/* Key Metrics */}
                    <div className="col-span-2 grid grid-cols-2 gap-4">
                        {report.mindScore.components && (
                            <>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.heartRate')}</div>
                                    <div className="text-2xl font-bold">{formatRounded(report.mindScore.components.HR_med)} <span className="text-sm font-normal text-gray-500">bpm</span></div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.rmssd')}</div>
                                    <div className="text-2xl font-bold">{report.mindScore.components?.RMSSD_med?.toFixed(1)} <span className="text-sm font-normal text-gray-500">ms</span></div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.stressLevel')}</div>
                                    <div className="text-xl font-bold">{t(`mentalHealth.stress.${report.stressAnalysis.category}`)}</div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.balance')}</div>
                                    <div className="text-xl font-bold">{translateValue(report.balanceAnalysis.flag, 'mentalHealth.balance')}</div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Interpretation */}
                <div data-pdf-block className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                    <h3 className="font-bold text-blue-900 mb-2">{t('mentalHealth.reportDetail.interpretation')}</h3>
                    <p className="text-blue-800 mb-2">{translateValue(report.stressAnalysis.description)}</p>
                    <p className="text-blue-800">{translateValue(report.balanceAnalysis.description)}</p>
                </div>
            </div>

            {/* Symptoms Section */}
            <div className="space-y-6 pt-8 break-inside-avoid">
                <h2 data-pdf-heading className="text-2xl font-bold border-l-4 border-indigo-600 pl-4">{t('mentalHealth.detail.symptoms')}</h2>
                {report.symptomPredictions ? (
                    <div className="grid grid-cols-2 gap-6">
                        <SymptomPredictionCard prediction={report.symptomPredictions.stress} />
                        <SymptomPredictionCard prediction={report.symptomPredictions.breathing} />
                        <SymptomPredictionCard prediction={report.symptomPredictions.pain} />
                        <SymptomPredictionCard prediction={report.symptomPredictions.depression} />
                        <SymptomPredictionCard prediction={report.symptomPredictions.cardiovascular} />
                        <SymptomPredictionCard prediction={report.symptomPredictions.bloodPressure} />
                    </div>
                ) : (
                    <div data-pdf-block className="text-center p-8 bg-gray-50 rounded-xl border border-gray-200">
                        {t('mentalHealth.detail.symptomPredictionNoData')}
                    </div>
                )}
            </div>

            {/* Charts Section */}
            {report.mindScore.selectedWindows && (
                <div className="space-y-6 pt-8 break-inside-avoid">
                    <h2 data-pdf-heading className="text-2xl font-bold border-l-4 border-purple-600 pl-4">{t('mentalHealth.detail.charts')}</h2>
                    <div className="grid grid-cols-2 gap-6">
                        <VitalSignsChart windows={report.mindScore.selectedWindows} type="hr" />
                        <VitalSignsChart windows={report.mindScore.selectedWindows} type="rmssd" />
                        <VitalSignsChart windows={report.mindScore.selectedWindows} type="resp" />
                        <VitalSignsChart windows={report.mindScore.selectedWindows} type="dqi" />
                    </div>
                </div>
            )}

            <div data-pdf-block className="text-center text-xs text-gray-400 pt-8 border-t mt-8">
                Generated by Carearly - {new Date().toLocaleString(intlLocale)}
            </div>
        </div>
    );
};

export default MentalHealthReportPDFTemplate;
